import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Çerez onay çubuğu — KVKK aydınlatma + ileride eklenecek reklam/ölçümleme
 * çerezleri için izin altyapısı.
 *
 * ÖNEMLİ — metin doğru olmalı: bu sitede şu an Google Analytics, piksel veya
 * reklam betiği YOK. Bu yüzden çubuk "kişisel verilerinizi izliyoruz" demiyor;
 * zorunlu çerezleri bildirip isteğe bağlı kategoriyi VARSAYILAN OLARAK KAPALI
 * tutuyor. Reklam/ölçümleme eklendiğinde betikler `hasOptionalConsent()`
 * arkasına alınmalı — izin verilmeden hiçbir isteğe bağlı çerez yazılmaz.
 */

const KEY = "ma_cookie_consent";
const VERSION = 1;

type Consent = { v: number; optional: boolean; ts: number };

function read(): Consent | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Consent;
    return p && p.v === VERSION ? p : null;
  } catch {
    // Gizli sekme / site verisi kapalı tarayıcı — sessizce yok say.
    return null;
  }
}

function write(optional: boolean) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ v: VERSION, optional, ts: Date.now() }));
  } catch {
    /* yazılamıyorsa sadece bu oturum için geçerli olur */
  }
}

/** Reklam/ölçümleme betikleri bunu kontrol etmeli. */
export function hasOptionalConsent(): boolean {
  return read()?.optional === true;
}

/** Footer'daki "Çerez tercihleri" bağlantısı bu olayı tetikler. */
export const REOPEN_EVENT = "ma:cookie-preferences";

export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (read() === null) setOpen(true);
    const reopen = () => setOpen(true);
    window.addEventListener(REOPEN_EVENT, reopen);
    return () => window.removeEventListener(REOPEN_EVENT, reopen);
  }, []);

  if (!open) return null;

  const decide = (optional: boolean) => {
    write(optional);
    setOpen(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Çerez tercihleri"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-gray-200 bg-white/95 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs leading-relaxed text-gray-600 sm:text-sm">
          Bu sitede oturumun ve tercihlerin çalışması için <strong>zorunlu çerezler</strong> kullanılır.
          Reklam ve ölçümleme çerezleri yalnızca izin verirseniz kullanılır.{" "}
          <Link to="/gizlilik" className="font-medium text-brand-500 hover:underline">
            Gizlilik &amp; KVKK
          </Link>{" "}
          ·{" "}
          <Link to="/kullanim-sartlari" className="font-medium text-brand-500 hover:underline">
            Kullanım Şartları
          </Link>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => decide(false)}
            className="rounded-xl border border-gray-300 px-4 py-2.5 text-xs font-bold text-gray-700 transition hover:bg-gray-50 sm:text-sm"
          >
            Sadece zorunlu
          </button>
          <button
            type="button"
            onClick={() => decide(true)}
            className="rounded-xl bg-brand-500 px-5 py-2.5 text-xs font-bold text-white transition hover:bg-brand-600 sm:text-sm"
          >
            Tümünü kabul et
          </button>
        </div>
      </div>
    </div>
  );
}
